import React, { useEffect, useRef, useState } from "react";

/* Simulated progress for single-call AI jobs (no backend stream): rises fast
   early then eases toward `cap`, so it feels alive but never claims done.
   pct = cap × (1 − e^(−t/tau)) — with tau 18s: ~22% @5s, ~62% @20s, ~82% @40s. */
export function useSimulatedProgress(active, { cap = 92, tau = 18000 } = {}) {
  const [pct, setPct] = useState(0);
  const startRef = useRef(null);
  useEffect(() => {
    if (!active) { startRef.current = null; setPct(0); return undefined; }
    startRef.current = Date.now();
    const t = setInterval(() => {
      const el = Date.now() - startRef.current;
      setPct(Math.min(cap, Math.round(cap * (1 - Math.exp(-el / tau)))));
    }, 250);
    return () => clearInterval(t);
  }, [active, cap, tau]);
  return pct;
}

/* Stage-driven progress for polled export jobs: advances with the REAL job
   status instead of a pure time curve, so the donut never looks parked.
   queued → eases to ~15%; processing → creeps toward ~94% with a small
   always-moving floor (~0.25%/s late-stage); `done` snaps to 100%. */
function useStagedProgress(active, stage, done) {
  const [pct, setPct] = useState(0);
  const pctRef = useRef(0);
  useEffect(() => {
    if (!active) { pctRef.current = 0; setPct(0); return undefined; }
    if (done) { pctRef.current = 100; setPct(100); return undefined; }
    const cap = stage === "queued" ? 15 : 94;
    const gain = stage === "queued" ? 0.12 : 0.012;
    const t = setInterval(() => {
      const cur = pctRef.current;
      const next = Math.min(cap, cur + Math.max(0.06, (cap - cur) * gain));
      pctRef.current = next;
      setPct(Math.round(next));
    }, 250);
    return () => clearInterval(t);
  }, [active, stage, done]);
  return pct;
}

/* Full-screen overlay with the donut — shared by PPTX/PDF export waits.
   Pass `stage` ("queued" | "processing") to drive the donut from the real
   job status; omit it to keep the legacy simulated curve. After
   `longWaitMs` still busy, `longWaitSubtitle` replaces the subtitle to
   reassure the founder. `done` snaps to 100% before the overlay closes. */
export function ExportProgressOverlay({ active, done = false, stage, title, subtitle,
  longWaitSubtitle, longWaitMs = 45000, testid = "export-progress" }) {
  const simPct = useSimulatedProgress(active && !done && stage == null, { cap: 92, tau: 12000 });
  const stagedPct = useStagedProgress(active && stage != null, stage, done);
  const [longWait, setLongWait] = useState(false);
  useEffect(() => {
    if (!active || done) { setLongWait(false); return undefined; }
    const t = setTimeout(() => setLongWait(true), longWaitMs);
    return () => clearTimeout(t);
  }, [active, done, longWaitMs]);
  if (!active) return null;
  const pct = done ? 100 : (stage != null ? stagedPct : simPct);
  const sub = longWait && longWaitSubtitle ? longWaitSubtitle : subtitle;
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.82)", backdropFilter: "blur(4px)" }}
      data-testid={testid} aria-live="polite" aria-busy="true">
      <div className="rounded-3xl max-w-sm w-full p-8 text-center border"
        style={{ backgroundColor: "#0A2826", borderColor: "rgba(123,211,206,0.22)" }}>
        <ProgressDonut pct={pct} size={150} stroke={13} testid={`${testid}-donut`} />
        <div className="mt-6 text-white font-extrabold text-lg">{title}</div>
        {sub && <div className="mt-1.5 text-xs text-white/45" data-testid={`${testid}-subtitle`}>{sub}</div>}
      </div>
    </div>
  );
}

export function ProgressDonut({ pct, size = 160, stroke = 14, color = "#0A8B85",
  trackColor = "rgba(255,255,255,0.08)", textColor = "#7BD3CE", testid }) {
  const R = (size - stroke) / 2 - 2;
  const CIRC = 2 * Math.PI * R;
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }} data-testid={testid}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - clamped / 100)}
          style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dashoffset 300ms ease-out" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-3xl font-extrabold tracking-tight"
          style={{ color: textColor, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}
          data-testid={testid ? `${testid}-pct` : undefined}>
          {clamped}%
        </div>
      </div>
    </div>
  );
}
